<template>
  <v-card
    v-if="visible"
    class="attendance-log-editor-bar fill-height right-bar-overlay"
    data-testid="attendance-log-bulk-editor-bar"
    border
  >
    <v-window
      v-model="visible"
      :mandatory="false"
      eager
    >
      <v-window-item
        key="bulkEdit"
        value="bulkEdit"
      >
        <v-card
          class="fill-height"
          elevation="2"
          tile
        >
          <v-card-title class="d-flex bb-1 bc-very-light-grey fw-600 pa-5">
            <span tabindex="0">
              {{ $bct('Edit attendance') }} | {{ attendanceLogs.length }}
              {{ $bct(attendanceLogs.length > 1 ? 'records' : 'record') }}
            </span>
            <v-spacer />

            <v-btn
              @click="close"
              aria-label="Close bulk edit"
              class="focus-visible"
              icon="close"
              size="small"
              variant="text"
            />
          </v-card-title>

          <v-card-text
            class="pt-5"
            data-testid="bulk-edit-summary"
          >
            <p class="fs-22 c-grey-70">
              {{ $bct('Changes will apply to') }}
            </p>
            <v-expansion-panels class="d-flex justify-space-between pt-2">
              <v-expansion-panel class="bg-super-light-gray">
                <v-expansion-panel-title>
                  <span class="fs-16">{{ $bct('Selected children') }}</span>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                  <div
                    v-for="log in attendanceLogs"
                    :key="log.id"
                    class="fs-16 c-grey-70 mb-1"
                  >
                    • {{ log.name }} (ID: {{ log?.meta?.child?.external_id }})
                  </div>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-card-text>

          <template v-if="attendanceLogs.length > 0">
            <v-card-text
              v-for="question in questions"
              :key="`bulk-${question.id}`"
            >
              <QuestionWithField
                v-model="bulkFormData"
                :dense="true"
                :elevation="0"
                :hide-actions="true"
                :outlined="false"
                :paddingless="true"
                :processing="processing"
                :question="question"
                :readonly="false"
                :schema="schema?.definition"
                :very-dense="true"
              />
            </v-card-text>
          </template>
          <div class="d-flex justify-end pa-4">
            <v-btn
              @click="close"
              class="focus-very-visible mr-2"
              variant="outlined"
            >
              {{ $bct('Close') }}
            </v-btn>
            <v-btn
              @click="handleApplyChanges"
              :loading="processing"
              :ripple="false"
              class="focus-very-visible"
              color="primary"
              data-testid="apply-changes-button"
            >
              {{ $bct('Apply Changes') }}
            </v-btn>
          </div>
        </v-card>
      </v-window-item>
    </v-window>
  </v-card>
</template>

<script setup>
import QuestionWithField from '@/shared/components/form/QuestionWithField.vue';
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';

const eventBus = useEventBus();
const { api } = useApi();
const $bct = inject('translator');

const props = defineProps({
  attendanceLogs: {
    type: Array,
    required: true,
  },
  questions: {
    type: Array,
    required: true,
  },
  schema: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['change']);

const visible = ref(false);
const bulkFormData = ref({});
const processing = ref(false);

const handleApplyChanges = async () => {
  processing.value = true;

  const ids = props.attendanceLogs.map((log) => log.id);
  const response = await api.shared.attendanceLog.bulkUpdate(ids, bulkFormData.value);

  processing.value = false;

  if (response?.status === 200) {
    eventBus.chime(`Updated ${response.data.updated_count} attendance logs`);
    emit('change');
    close();
  }
};

const open = () => {
  bulkFormData.value = { custom: {} };
  visible.value = 'bulkEdit';
};

const close = () => {
  visible.value = false;
  bulkFormData.value = {};
};

const isOpen = () => !!visible.value;

defineExpose({
  open,
  close,
  isOpen,
});
</script>

<style scoped>
.attendance-log-editor-bar {
  overflow-y: auto;
  padding-top: 64px;
  position: fixed;
  top: 0;
  z-index: 1000;
  right: 0;
  width: 30%;
}

@media only screen and (max-width: 1200px) {
  .attendance-log-editor-bar {
    overflow-y: auto;
    padding-top: 64px;
    padding-bottom: 72px;
    position: fixed;
    top: 0;
    z-index: 1000;
    right: 0;
    width: 50%;
  }
}

@media only screen and (max-width: 800px) {
  .attendance-log-editor-bar {
    overflow-y: auto;
    padding-top: 64px;
    padding-bottom: 72px;
    position: fixed;
    top: 0;
    z-index: 1000;
    right: 0;
    width: 100%;
  }
}
</style>
